<template>
  <div class="new-student-dashboard-page">
    <app-layout>
      <template v-slot:header>
        <Header :title="$t('new_student_dashboard')">
          <template v-slot:actions>
            <b-button
              variant="outline-success"
              size="sm"
              class="d-flex align-items-center gap-2 shadow-sm font-weight-600"
              @click="exportExcel"
              :disabled="exportLoading"
            >
              <i v-if="exportLoading" class="ri-loader-4-line ri-spin"></i>
              <i v-else class="ri-file-excel-2-line"></i>
              <span>{{ $t('download_excel_report') }}</span>
            </b-button>
          </template>
        </Header>
      </template>

      <template v-slot:header-mobile>
        <HeaderMobile :title="$t('new_student_dashboard')" />
      </template>

      <div class="container-fluid py-3 px-4">

        <div class="dashboard-filter-card card border-0 shadow-sm rounded-12 mb-4">
          <div class="card-body p-3">
            <b-row class="align-items-end g-3">
              <b-col sm="12" md="6" lg="3" xl="2">
                <label class="filter-label text-muted font-weight-600 small mb-1">
                  {{ $t('academic_year') }}
                </label>
                <academic-years-selectbox
                  v-model="filters.academic_year"
                  :activeYears="true"
                />
              </b-col>

              <b-col sm="12" md="6" lg="3" xl="2">
                <label class="filter-label text-muted font-weight-600 small mb-1">
                  {{ $t('semester') }}
                </label>
                <semesters-selectbox
                  v-model="filters.semester"
                />
              </b-col>

              <b-col sm="12" md="6" lg="3" xl="2">
                <label class="filter-label text-muted font-weight-600 small mb-1">
                  {{ $t('start_date') }}
                </label>
                <select-date v-model="filters.start_date" />
              </b-col>

              <b-col sm="12" md="6" lg="3" xl="2">
                <label class="filter-label text-muted font-weight-600 small mb-1">
                  {{ $t('end_date') }}
                </label>
                <select-date v-model="filters.end_date" />
              </b-col>

              <b-col sm="12" md="12" lg="12" xl="4" class="d-flex align-items-center gap-2 mt-3 mt-xl-0">
                <button
                  type="button"
                  class="btn btn-update flex-grow-1 font-weight-600 shadow-sm d-flex align-items-center justify-content-center border-0 text-white"
                  @click="fetchDashboardData"
                  :disabled="loading"
                >
                  <i v-if="loading" class="ri-loader-4-line ri-spin mr-1 text-white"></i>
                  <i v-else class="ri-refresh-line mr-1 text-white"></i>
                  <span class="text-white font-weight-700">{{ $t('update') }}</span>
                </button>

                <button
                  type="button"
                  class="btn btn-excel flex-grow-1 font-weight-600 shadow-sm d-flex align-items-center justify-content-center border-0 text-white"
                  @click="exportExcel"
                  :disabled="exportLoading"
                  v-b-tooltip.hover
                  :title="$t('excel_export')"
                >
                  <i v-if="exportLoading" class="ri-loader-4-line ri-spin mr-1 text-white"></i>
                  <i v-else class="ri-file-excel-2-line mr-1 text-white"></i>
                  <span class="text-white font-weight-700">{{ $t('excel_export') }}</span>
                </button>

                <b-button
                  variant="light"
                  class="btn-reset font-weight-600 text-secondary border d-flex align-items-center justify-content-center flex-shrink-0"
                  @click="clearFilters"
                  v-b-tooltip.hover
                  :title="$t('clear_filters')"
                >
                  <i class="ri-filter-off-line"></i>
                </b-button>
              </b-col>
            </b-row>
          </div>
        </div>

        <div class="kpi-section mb-4">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h5 class="section-title mb-0 font-weight-700 text-dark d-flex align-items-center">
              <i class="ri-dashboard-3-line mr-2 text-success"></i>
              {{ $t('student_registration_summaries') }}
            </h5>
            <span v-if="lastUpdated" class="text-muted small">
              <i class="ri-time-line mr-1"></i>{{ $t('dashboard_last_updated') }}: {{ lastUpdated }}
            </span>
          </div>

          <div v-if="loading" class="d-flex flex-column align-items-center justify-content-center py-5 my-5">
            <b-spinner variant="success" style="width: 3rem; height: 3rem;" class="mb-3"></b-spinner>
            <p class="font-weight-600 text-muted mb-0 small">{{ $t('loading') }}</p>
          </div>

          <b-row v-else class="g-3">
            <b-col
              v-for="card in kpiCards"
              :key="card.category"
              cols="12"
              sm="6"
              md="4"
              lg="3"
            >
              <div
                class="kpi-card card border-0 shadow-sm rounded-16 h-100 transition-all position-relative overflow-hidden"
                :class="`kpi-card-${card.theme}`"
              >
                <div class="kpi-top-bar" :style="{ backgroundColor: card.color }"></div>

                <div class="card-body p-3.5 d-flex flex-column justify-content-between">
                  <div>
                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <span
                        class="kpi-icon-wrapper rounded-circle d-flex align-items-center justify-content-center"
                        :style="{ backgroundColor: card.bgColor, color: card.color }"
                      >
                        <i :class="card.icon"></i>
                      </span>
                      <span class="badge rounded-pill px-2.5 py-1 font-weight-600 text-uppercase" :style="{ backgroundColor: card.bgColor, color: card.color }">
                        {{ $t(card.badgeTextKey) }}
                      </span>
                    </div>

                    <h6 class="kpi-title text-muted font-weight-600 mb-1">
                      {{ $t(card.titleKey) }}
                    </h6>

                    <div class="kpi-value-container my-2">
                      <span class="kpi-value font-weight-800 text-dark">
                        {{ getCardDisplayValue(card.category) }}
                      </span>
                    </div>
                  </div>

                  <div class="mt-3">
                    <button
                      type="button"
                      class="btn btn-details block w-100 rounded-10 font-weight-600 shadow-sm d-flex align-items-center justify-content-center py-2 text-white border-0"
                      :style="{ backgroundColor: card.color }"
                      @click="openCategoryDetails(card)"
                    >
                      <span class="text-white">{{ $t('show_details') }}</span>
                      <i class="ri-arrow-right-line ml-1 transition-icon text-white"></i>
                    </button>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
    </app-layout>

    <b-modal
      v-model="modal.show"
      size="lg"
      scrollable
      centered
      hide-footer
      header-class="border-0 p-4 pb-2"
      body-class="p-4"
    >
      <template #modal-header>
        <div class="d-flex align-items-center justify-content-between w-100">
          <div class="d-flex align-items-center gap-3">
            <span
              class="modal-icon-wrapper rounded-circle d-flex align-items-center justify-content-center"
              :style="{ backgroundColor: modal.cardInfo ? modal.cardInfo.bgColor : '#eef2ff', color: modal.cardInfo ? modal.cardInfo.color : '#4f46e5' }"
            >
              <i :class="modal.cardInfo ? modal.cardInfo.icon : 'ri-user-search-line'"></i>
            </span>
            <div>
              <h5 class="modal-title font-weight-800 text-dark mb-0">
                {{ modal.cardInfo ? $t(modal.cardInfo.titleKey) : $t('student_details') }}
              </h5>
              <span class="text-muted small">
                {{ $t('registered_student_count', { count: modal.items.length }) }}
              </span>
            </div>
          </div>

          <button type="button" class="close text-muted" @click="modal.show = false">
            <i class="ri-close-line"></i>
          </button>
        </div>
      </template>

      <div class="mb-3">
        <b-input-group size="sm" class="rounded-10 shadow-sm border-0">
          <template #prepend>
            <b-input-group-text class="bg-white border-right-0 text-muted pl-3">
              <i class="ri-search-line"></i>
            </b-input-group-text>
          </template>
          <b-form-input
            v-model="modal.searchQuery"
            :placeholder="$t('search_student_placeholder')"
            class="border-left-0 pl-0 rounded-right-10"
          ></b-form-input>
        </b-input-group>
      </div>

      <div v-if="modal.loading" class="text-center py-5">
        <b-spinner variant="success" label="Loading..."></b-spinner>
        <p class="text-muted small mt-2">{{ $t('loading_student_details') }}</p>
      </div>

      <div v-else-if="filteredModalItems && filteredModalItems.length > 0" class="table-responsive rounded-12 border">
        <table class="table table-hover align-middle mb-0">
          <thead class="bg-light text-secondary font-weight-700 small">
            <tr>
              <th scope="col" class="pl-3 py-2.5">#</th>
              <th scope="col" class="py-2.5">{{ $t('name_surname') }}</th>
              <th scope="col" class="py-2.5">{{ $t('program') }}</th>
              <th scope="col" class="py-2.5">{{ $t('scholarship') }}</th>
              <th scope="col" class="pr-3 py-2.5">{{ $t('description') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(student, idx) in filteredModalItems" :key="`student-item-${student.student_number || 'num'}-${idx}`">
              <td class="pl-3 text-muted font-weight-600 small">{{ idx + 1 }}</td>
              <td class="font-weight-700 text-dark text-capitalize">
                {{ student.name }} {{ student.surname }}
              </td>
              <td class="text-secondary font-weight-500">
                {{ student.program }}
              </td>
              <td>
                <span class="badge badge-light-primary font-weight-600">
                  {{ student.scholarship }}
                  <template v-if="student.scholarship_rate > 0">
                    (%{{ student.scholarship_rate }})
                  </template>
                </span>
              </td>
              <td class="pr-3 text-muted small">
                {{ student.scholarship_description || '-' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-else class="text-center py-5 text-muted">
        <i class="ri-user-unfollow-line ri-2x mb-2 d-block"></i>
        <p class="font-weight-600 mb-0">{{ $t('no_student_details_found') }}</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import RegistrationService from "@/services/RegistrationService";

export default {
  name: "NewStudentDashboard",
  data() {
    return {
      loading: false,
      exportLoading: false,
      lastUpdated: null,

      // Query Filters
      filters: {
        academic_year: null,
        semester: null,
        start_date: null,
        end_date: null
      },

      // Raw API metrics response data
      dashboardMetrics: {
        today: 0,
        all: "0 / 0 (%0.00)",
        osym_100: 0,
        osym_75: 0,
        osym_50: 0,
        without_scholarship: 0,
        special_scholarship: 0
      },

      // KPI Metric Cards Configurations
      kpiCards: [
        {
          category: "today",
          titleKey: "today_registered_students",
          badgeTextKey: "daily",
          icon: "ri-user-add-line",
          color: "#16a34a",
          bgColor: "#dcfce7",
          theme: "green"
        },
        {
          category: "all",
          titleKey: "all_registrations",
          badgeTextKey: "total",
          icon: "ri-group-line",
          color: "#2563eb",
          bgColor: "#dbeafe",
          theme: "blue"
        },
        {
          category: "osym_100",
          titleKey: "osym_100_scholarship",
          badgeTextKey: "osym_100_scholarship",
          icon: "ri-medal-fill",
          color: "#9333ea",
          bgColor: "#f3e8ff",
          theme: "purple"
        },
        {
          category: "osym_75",
          titleKey: "osym_75_scholarship",
          badgeTextKey: "osym_75_scholarship",
          icon: "ri-award-line",
          color: "#0284c7",
          bgColor: "#e0f2fe",
          theme: "cyan"
        },
        {
          category: "osym_50",
          titleKey: "osym_50_scholarship",
          badgeTextKey: "osym_50_scholarship",
          icon: "ri-percent-line",
          color: "#d97706",
          bgColor: "#fef3c7",
          theme: "amber"
        },
        {
          category: "without_scholarship",
          titleKey: "without_scholarship",
          badgeTextKey: "without_scholarship",
          icon: "ri-user-star-line",
          color: "#dc2626",
          bgColor: "#fee2e2",
          theme: "red"
        },
        {
          category: "special_scholarship",
          titleKey: "special_scholarship",
          badgeTextKey: "special_scholarship",
          icon: "ri-vip-crown-line",
          color: "#0d9488",
          bgColor: "#ccfbf1",
          theme: "teal"
        }
      ],

      // Modal State
      modal: {
        show: false,
        loading: false,
        cardInfo: null,
        items: [],
        searchQuery: ""
      }
    };
  },
  computed: {
    filteredModalItems() {
      if (!this.modal.searchQuery) return this.modal.items;
      const q = this.modal.searchQuery.toLowerCase().trim();
      return this.modal.items.filter(item => {
        const fullName = `${item.name || ''} ${item.surname || ''}`.toLowerCase();
        const prog = String(item.program || "").toLowerCase();
        const scholarship = String(item.scholarship || "").toLowerCase();
        return fullName.includes(q) || prog.includes(q) || scholarship.includes(q);
      });
    }
  },
  created() {
    this.fetchDashboardData();
  },
  methods: {
    async fetchDashboardData() {
      this.loading = true;
      try {
        const params = this.cleanParams(this.filters);
        const response = await RegistrationService.getNewStudentDashboard({ params });

        if (response && response.data) {
          const res = response.data.data || response.data;

          // Set Metrics Cards Data
          if (res.metrics) {
            this.dashboardMetrics = { ...this.dashboardMetrics, ...res.metrics };
          } else {
            this.dashboardMetrics = { ...this.dashboardMetrics, ...res };
          }
        }
        this.lastUpdated = this.$moment().format("HH:mm:ss");
      } catch (err) {
        const errorData = err.response ? err.response.data : (err.data || err);
        if (errorData && errorData.message) {
          this.$toast.error(this.$t('api.' + errorData.message));
        }
      } finally {
        this.loading = false;
      }
    },

    async openCategoryDetails(card) {
      this.modal.cardInfo = card;
      this.modal.show = true;
      this.modal.loading = true;
      this.modal.items = [];
      this.modal.searchQuery = "";

      try {
        const params = {
          ...this.cleanParams(this.filters),
          category: card.category
        };
        const response = await RegistrationService.getNewStudentDashboardDetails({ params });

        if (response && response.data) {
          const list = response.data.data || response.data;
          this.modal.items = Array.isArray(list) ? list : [];
        }
      } catch (err) {
        const errorData = err.response ? err.response.data : (err.data || err);
        if (errorData && errorData.message) {
          this.$toast.error(this.$t('api.' + errorData.message));
        }
      } finally {
        this.modal.loading = false;
      }
    },

    exportExcel() {
      this.exportLoading = true;
      const config = {
        params: this.cleanParams(this.filters)
      };

      return RegistrationService.exportNewStudentDashboardExcel(config)
        .then((response) => {
          this._downloadFile(response, `${this.$t('new_student_dashboard')}.xlsx`);
        })
        .catch((err) => {
          const errorData = err.response ? err.response.data : (err.data || err);
          if (errorData && errorData.message) {
            this.$toast.error(this.$t('api.' + errorData.message));
          }
        })
        .finally(() => {
          this.exportLoading = false;
        });
    },

    clearFilters() {
      this.filters = {
        academic_year: null,
        semester: null,
        start_date: null,
        end_date: null
      };
      this.fetchDashboardData();
    },

    cleanParams(obj) {
      const cleaned = {};
      Object.keys(obj).forEach(key => {
        if (obj[key] !== null && obj[key] !== undefined && obj[key] !== "") {
          cleaned[key] = obj[key];
        }
      });
      return cleaned;
    },

    getCardDisplayValue(category) {
      const val = this.dashboardMetrics[category];
      if (val === undefined || val === null) return "0";
      if (typeof val === "object" && val.count !== undefined) {
        if (val.total !== undefined) {
          return `${val.count} / ${val.total} (${val.ratio || "%0"})`;
        }
        return val.count;
      }
      return val;
    }
  }
};
</script>

<style lang="scss" scoped>
.new-student-dashboard-page {
  background-color: #f8fafc;
  min-height: 100vh;
}

.rounded-12 {
  border-radius: 12px !important;
}

.rounded-16 {
  border-radius: 16px !important;
}

.rounded-10 {
  border-radius: 10px !important;
}

/* Filter Card */
.dashboard-filter-card {
  background: #ffffff;
  border: 1px solid #e2e8f0 !important;
}

.btn-update, .btn-excel {
  height: 38px;
  white-space: nowrap;
  transition: all 0.2s ease;

  *, span, i {
    color: #ffffff !important;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

.btn-update {
  background-color: #16a34a !important;
  border-color: #16a34a !important;
  color: #ffffff !important;

  &:hover, &:focus, &:active {
    background-color: #15803d !important;
    border-color: #15803d !important;
    color: #ffffff !important;
    transform: translateY(-1px);
  }
}

.btn-excel {
  background-color: #2563eb !important;
  border-color: #2563eb !important;
  color: #ffffff !important;

  &:hover, &:focus, &:active {
    background-color: #1d4ed8 !important;
    border-color: #1d4ed8 !important;
    color: #ffffff !important;
    transform: translateY(-1px);
  }
}

.btn-reset {
  height: 38px;
  width: 38px;
  padding: 0;
  border-radius: 10px !important;
  &:hover {
    background-color: #f1f5f9;
  }
}

@media (max-width: 575.98px) {
  .btn-excel {
    width: 38px;
    min-width: 38px;
    flex-grow: 0 !important;
    padding: 0 !important;

    span {
      display: none !important;
    }

    i {
      margin-right: 0 !important;
    }
  }
}

/* Category Filter Tabs */
.filter-tab-btn {
  font-size: 0.825rem;
  border: 1px solid transparent !important;
  transition: all 0.2s ease;
}

.filter-tab-active {
  background-color: #16a34a !important;
  border-color: #16a34a !important;
  color: #ffffff !important;
  box-shadow: 0 2px 6px rgba(22, 163, 74, 0.3) !important;
}

.filter-tab-inactive {
  background-color: #e2e8f0 !important;
  border-color: #cbd5e1 !important;
  color: #475569 !important;

  &:hover {
    background-color: #cbd5e1 !important;
    color: #0f172a !important;
  }
}

.filter-tab-toggle {
  background-color: #ffffff !important;
  border: 1px solid #cbd5e1 !important;
  color: #475569 !important;
  font-size: 0.825rem;

  &:hover {
    background-color: #f8fafc !important;
    color: #0f172a !important;
    border-color: #94a3b8 !important;
  }
}

/* KPI Cards */
.kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0 !important;
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px -10px rgba(0, 0, 0, 0.08) !important;

    .btn-details .transition-icon {
      transform: translateX(4px);
    }
  }

  .kpi-top-bar {
    height: 4px;
    width: 100%;
  }

  .kpi-icon-wrapper {
    width: 42px;
    height: 42px;
    font-size: 1.25rem;
  }

  .kpi-title {
    font-size: 0.875rem;
  }

  .kpi-value {
    font-size: 1.65rem;
    letter-spacing: -0.02em;
    line-height: 1.2;
  }

  .btn-details {
    font-size: 0.85rem;
    transition: all 0.2s ease;
    color: #ffffff !important;

    *, span, i {
      color: #ffffff !important;
    }

    .transition-icon {
      transition: transform 0.2s ease;
    }
  }
}

/* Table Section */
.custom-dashboard-table {
  thead th {
    border-bottom: 2px solid #e2e8f0;
    font-size: 0.78rem;
    letter-spacing: 0.03em;
  }

  tbody tr {
    transition: background-color 0.15s ease;
  }

  .program-item-row:hover {
    background-color: #f8fafc;
  }

  .bg-light-green {
    background-color: #f0fdf4 !important;
    border-top: 1px solid #bbf7d0;
    border-bottom: 2px solid #86efac;
  }
}

/* Modal styling */
.modal-icon-wrapper {
  width: 44px;
  height: 44px;
  font-size: 1.3rem;
}

.badge-light-primary {
  background-color: #e0e7ff;
  color: #3730a3;
}
.badge-light-info {
  background-color: #e0f2fe;
  color: #0369a1;
}
.badge-light-warning {
  background-color: #fef3c7;
  color: #92400e;
}
.badge-light-danger {
  background-color: #fee2e2;
  color: #991b1b;
}
.badge-light-success {
  background-color: #dcfce7;
  color: #166534;
}

.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 1rem;
}
</style>
